import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout/PageDetailLayout';
import { Button, Card, Badge, Table, Timeline, ConfirmDialog } from '@/components/ui';
import { useAdminUserDetail, useAdminUserActions, useAdminUserAuditLogs } from './hooks/useUsers';
import { useAuth } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui';
import { getRoleIcon, getRoleBadgeVariant } from '@/utils/role';

const TABS = [
  { value: 'overview', label: 'Hồ sơ Định danh', icon: 'ri-profile-line' },
  { value: 'tenants', label: 'Chìa khóa Truy cập', icon: 'ri-key-2-line' },
  { value: 'activity', label: 'Telemetry & Audit', icon: 'ri-radar-line' },
];

// Helper: Hiệu ứng Status Dot (Premium)
const PulsingStatusDot = ({ isActive }: { isActive: boolean }) => (
  isActive ? (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 shadow-sm">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
      </span>
      <span className="text-xs font-medium text-emerald-700">Đang hoạt động</span>
    </div>
  ) : (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
      <span className="relative flex h-2 w-2">
        <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
      </span>
      <span className="text-xs font-medium text-slate-600">Bị vô hiệu hóa</span>
    </div>
  )
);

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();

  const { data: result, isLoading } = useAdminUserDetail(id);
  const { toggleLock } = useAdminUserActions();
  const { data: auditResult, isLoading: isAuditLoading } = useAdminUserAuditLogs(id);

  const user = result?.data;
  const auditLogs = auditResult?.data || [];

  const timelineItems = auditLogs.length > 0 
    ? auditLogs.map((log: any, index: number) => ({
        id: log.id,
        title: log.action_type === 'INSERT' ? 'Khởi tạo Dữ liệu' : log.action_type === 'UPDATE' ? 'Cập nhật Dữ liệu' : log.action_type === 'DELETE' ? 'Xóa Dữ liệu' : log.action_type,
        description: `Bảng tác động: ${log.table_name || 'Hệ thống'}`,
        time: new Date(log.created_at).toLocaleString('vi-VN'),
        status: 'completed' as const,
        icon: log.action_type === 'INSERT' ? 'ri-add-circle-line' : log.action_type === 'UPDATE' ? 'ri-edit-circle-line' : log.action_type === 'DELETE' ? 'ri-delete-bin-line' : 'ri-radar-line'
      }))
    : [
        {
          id: 1,
          title: 'Chưa có hoạt động',
          description: 'Hệ thống chưa ghi nhận dấu chân kỹ thuật số nào từ tài khoản này.',
          time: 'Hiện tại',
          status: 'completed' as const,
          icon: 'ri-time-line'
        }
      ];

  const { impersonateUser, isImpersonating: globalIsImpersonating } = useAuth();
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);

  const handleImpersonate = async () => {
    if (!user) return;
    try {
      await impersonateUser(user as any);
      toast.success(`Đã thiết lập phiên nhập vai: ${user.full_name}`);
      setShowImpersonateConfirm(false);
    } catch (error) {
      toast.error('Giao thức Nhập vai thất bại');
      console.error(error);
    }
  };

  if (!id) return null;

  const isPersonal = !user?.company_id || !user?.company_name || user?.company_name.toLowerCase().includes('cá nhân');

  return (
    <PageDetailLayout
      title={
        <div className="flex items-center gap-3">
          <span className="text-xl font-medium tracking-tight text-slate-900">{user?.full_name || 'Hồ sơ Đặc quyền'}</span>
          {user && <PulsingStatusDot isActive={user.is_active} />}
        </div>
      }
      description={<span className="text-sm text-slate-500 font-medium">{user?.email || 'Global Security Matrix'}</span>}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isLoading={isLoading}
      headerActions={
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="border-amber-200 bg-amber-50/50 text-amber-700 hover:bg-amber-100 hover:border-amber-300 shadow-sm transition-all"
            onClick={() => setShowImpersonateConfirm(true)}
            disabled={globalIsImpersonating || user?.role_code === 'super_admin'}
          >
            <i className="ri-spy-line mr-2 text-lg" />
            <span className="font-medium">Nhập vai</span>
          </Button>
          
          <div className="w-px h-6 bg-slate-200 mx-1"></div>

          <Button
            variant={user?.is_active ? 'outline' : 'primary'}
            className={user?.is_active 
              ? 'text-rose-600 border-rose-200 bg-rose-50/50 hover:bg-rose-100 transition-all shadow-sm' 
              : 'bg-emerald-600 hover:bg-emerald-700 shadow-sm'}
            onClick={() => user && toggleLock({ id: user.id, isActive: !user.is_active })}
          >
            <i className={user?.is_active ? 'ri-shield-keyhole-line mr-2' : 'ri-shield-check-line mr-2'} />
            <span className="font-medium">{user?.is_active ? 'Đóng băng' : 'Mở khóa'}</span>
          </Button>
          
          <Button
            variant="primary"
            className="shadow-sm"
            onClick={() => navigate(`/admin/users/edit/${id}`)}
          >
            <i className="ri-pencil-ruler-2-line mr-2" />
            <span className="font-medium">Hiệu chỉnh</span>
          </Button>
        </div>
      }
      image={
        <div className="w-20 h-20 bg-gradient-to-br from-brand-100 to-brand-50 rounded-2xl flex items-center justify-center border border-brand-200/50 text-brand-600 font-medium text-3xl shadow-sm overflow-hidden p-1">
          {user?.avatar_path ? (
            <img src={user.avatar_path} alt="" className="w-full h-full rounded-[14px] object-cover" />
          ) : (
            <span className="drop-shadow-sm">{user?.full_name?.charAt(0) || 'U'}</span>
          )}
        </div>
      }
    >
      {activeTab === 'overview' && user && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex flex-col lg:flex-row gap-6">
            
            {/* Cột 1: Thông tin Lõi (Tự động co giãn) */}
            <div className="flex-1 min-w-0 space-y-6">
              <Card className="p-7 rounded-2xl border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                      <i className="ri-fingerprint-line text-lg" />
                    </div>
                    <h3 className="text-base font-medium text-slate-800 tracking-tight">Định danh Lõi (Core Identity)</h3>
                  </div>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-500 font-medium border-none px-3 py-1 rounded-lg">
                    UID: {user.id.split('-')[0].toUpperCase()}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
                  {/* Item: Tổ chức (Nổi bật) */}
                  <div className="sm:col-span-2 p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1.5">
                        <i className="ri-building-4-line text-slate-300" />
                        Trực thuộc Tổ chức
                      </p>
                      <div className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${isPersonal ? 'bg-white border-slate-200 shadow-sm' : 'bg-brand-50/50 border-brand-100 shadow-sm'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium border ${isPersonal ? 'bg-slate-50 text-slate-500 border-slate-100' : 'bg-white text-brand-600 border-brand-100 shadow-sm'}`}>
                          {user.company_code ? user.company_code.charAt(0) : '👤'}
                        </div>
                        <div>
                          <p className={`text-sm font-medium leading-none ${isPersonal ? 'text-slate-600' : 'text-brand-800'}`}>
                            {user.company_name || 'Khách / Cá nhân'}
                          </p>
                          <p className={`text-xs font-medium mt-1 ${isPersonal ? 'text-slate-400' : 'text-brand-600/70'}`}>
                            {user.company_code || 'GUEST_ACCOUNT'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="hidden sm:block w-px h-12 bg-slate-200 mx-2" />

                    <div>
                      <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1.5">
                        <i className="ri-shield-user-line text-slate-300" />
                        Cấp bậc / Vai trò
                      </p>
                      <Badge variant={getRoleBadgeVariant(user.role_code)} className="font-medium px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm shadow-sm border border-current/10">
                        <i className={`${getRoleIcon(user.role_code)} text-lg opacity-80`} />
                        {user.role_name || user.role_code}
                      </Badge>
                    </div>
                  </div>

                  <div className="group">
                    <p className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">
                      <i className="ri-user-smile-line text-slate-300" /> Họ và tên
                    </p>
                    <p className="text-sm font-medium text-slate-800">{user.full_name}</p>
                  </div>
                  
                  <div className="group">
                    <p className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">
                      <i className="ri-mail-send-line text-slate-300" /> Địa chỉ Email
                    </p>
                    <p className="text-sm font-medium text-slate-800">{user.email}</p>
                  </div>

                  <div className="group">
                    <p className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">
                      <i className="ri-phone-line text-slate-300" /> Điện thoại liên hệ
                    </p>
                    <p className="text-sm font-medium text-slate-800">{user.phone || 'Chưa cập nhật'}</p>
                  </div>

                  <div className="group">
                    <p className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">
                      <i className="ri-briefcase-4-line text-slate-300" /> Chức danh (Nội bộ)
                    </p>
                    <p className="text-sm font-medium text-slate-800">{user.job_title || 'N/A'}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Cột 2: Bảo mật & Ghi chú (Kích thước cố định) */}
            <div className="w-full lg:w-[420px] shrink-0 space-y-6">
              <Card className="p-6 rounded-2xl border-slate-200/60 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-emerald-500" />
                <div className="flex items-center gap-2.5 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <i className="ri-shield-check-line text-lg" />
                  </div>
                  <h3 className="text-base font-medium text-slate-800 tracking-tight">Hàng rào Bảo mật</h3>
                </div>
                
                <div className="space-y-5">
                  <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100 transition-colors hover:bg-slate-100/50">
                    <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                      <i className="ri-key-2-line" /> Mật khẩu
                    </span>
                    <span className="text-xs font-medium text-slate-700">Đã thiết lập</span>
                  </div>
                  <div className="flex justify-between items-center bg-amber-50/50 p-3 rounded-xl border border-amber-100 transition-colors">
                    <span className="text-xs font-medium text-amber-700/70 flex items-center gap-1.5">
                      <i className="ri-smartphone-line" /> Xác thực 2 bước
                    </span>
                    <Badge variant="custom" className="bg-white border border-amber-200 text-amber-600 font-medium text-xs px-2.5 py-0.5 rounded-lg shadow-sm">
                      Chưa kích hoạt
                    </Badge>
                  </div>
                </div>
              </Card>

              {/* Private Notes (Super Admin) */}
              <Card className="p-6 rounded-2xl bg-gradient-to-b from-slate-900 to-slate-800 text-white relative overflow-hidden shadow-lg border border-slate-700">
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
                    <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    <h3 className="text-sm font-medium text-slate-300">Private Notes (Super Admin)</h3>
                  </div>
                  <p className="text-sm text-slate-300/90 italic leading-relaxed font-serif">
                    "Tài khoản này được cấp quyền đặc biệt để xử lý chiến dịch kiểm toán Q2. Cần theo dõi hành vi truy cập dữ liệu nhạy cảm. Thu hồi quyền hạn vào cuối tháng 6."
                  </p>
                </div>
                <i className="ri-lock-password-line absolute -bottom-6 -right-6 text-8xl text-white/5 rotate-[-15deg] pointer-events-none" />
              </Card>
            </div>
            
          </div>
        </div>
      )}

      {activeTab === 'tenants' && user && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="p-0 overflow-hidden border-slate-200/60 shadow-sm rounded-2xl">
            <div className="p-6 border-b border-slate-100 bg-slate-50/30 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <i className="ri-key-2-line text-xl" />
              </div>
              <div>
                <h3 className="text-base font-medium text-slate-800 tracking-tight">Chìa khóa Truy cập (Workspaces)</h3>
                <p className="text-xs text-slate-500 font-medium mt-1">Danh sách các không gian làm việc mà nhân sự này có quyền can thiệp.</p>
              </div>
            </div>
            <Table
              data={[
                {
                  company: user.company_name,
                  code: user.company_code,
                  role: user.role_name,
                  joined: user.created_at,
                  status: user.is_active
                }
              ]}
              columns={[
                {
                  key: 'company',
                  label: 'Không gian (Workspace)',
                  render: (v, row) => (
                    <div className="flex items-center gap-3 py-2">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center font-medium text-brand-600 border border-slate-200 shadow-sm">
                        {row.code?.charAt(0) || '👤'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{v || 'Khách / Cá nhân'}</p>
                        <p className="text-xs text-slate-400 font-mono tracking-tight mt-0.5">{row.code || 'GUEST'}</p>
                      </div>
                    </div>
                  )
                },
                {
                  key: 'role', label: 'Quyền hạn (Role)', render: (v, row) => (
                    <Badge variant={getRoleBadgeVariant(row.role_code)} className="font-medium flex items-center gap-1.5 px-3 py-1 rounded-lg w-fit">
                      <i className={getRoleIcon(row.role_code)} />
                      {v || row.role_code}
                    </Badge>
                  )
                },
                { key: 'joined', label: 'Cấp phát lúc', render: (v) => <span className="text-sm text-slate-600 font-medium">{new Date(v).toLocaleDateString('vi-VN')}</span> },
                { key: 'status', label: 'Tình trạng', render: (v) => <PulsingStatusDot isActive={v} /> },
              ]}
            />
          </Card>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="flex flex-col lg:flex-row gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="flex-1 min-w-0 p-7 rounded-2xl border-slate-200/60 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <i className="ri-radar-line text-xl" />
              </div>
              <div>
                <h3 className="text-base font-medium text-slate-800 tracking-tight">Radar & Audit Log</h3>
                <p className="text-xs text-slate-500 font-medium mt-1">Giám sát dấu chân kỹ thuật số của nhân sự trên toàn hệ thống.</p>
              </div>
            </div>
            
            <div className={isAuditLoading ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
              <Timeline items={timelineItems} />
            </div>
          </Card>

          <div className="w-full lg:w-[420px] shrink-0 space-y-6">
            <Card className="p-6 rounded-2xl bg-slate-50/50 border-dashed border-2 border-slate-200">
              <div className="flex items-center gap-2 mb-6">
                <i className="ri-macbook-line text-slate-400" />
                <h4 className="text-sm font-medium text-slate-600 tracking-tight">Môi trường Truy cập</h4>
              </div>
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between text-xs font-medium text-slate-600 mb-2">
                    <span className="flex items-center gap-1.5"><i className="ri-windows-line" /> Windows / Chrome</span>
                    <span className="text-slate-500 bg-white px-2 py-0.5 rounded shadow-sm border border-slate-100">90%</span>
                  </div>
                  <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden shadow-inner">
                    <div className="bg-brand-500 h-full w-[90%] rounded-full relative overflow-hidden">
                      <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite] -skew-x-12" />
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between text-xs font-medium text-slate-600 mb-2">
                    <span className="flex items-center gap-1.5"><i className="ri-apple-line" /> iOS / Safari</span>
                    <span className="text-slate-500 bg-white px-2 py-0.5 rounded shadow-sm border border-slate-100">10%</span>
                  </div>
                  <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden shadow-inner">
                    <div className="bg-slate-400 h-full w-[10%] rounded-full" />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      <ConfirmDialog
        isOpen={showImpersonateConfirm}
        title="Kích hoạt Giao thức Nhập vai"
        message={
          <div className="space-y-4">
            <p className="text-sm font-medium text-slate-600">Bạn chuẩn bị truy cập hệ thống dưới vỏ bọc định danh của <strong className="text-slate-900">{user?.full_name}</strong>.</p>
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200/60 shadow-sm">
              <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                <i className="ri-shield-star-line text-amber-600 text-lg" /> Cảnh báo Bảo mật:
              </p>
              <p className="text-xs font-medium text-amber-700/80 leading-relaxed">
                Mọi hành vi thao tác dữ liệu trong phiên làm việc này sẽ được ghi nhận vào Audit Log bằng tài khoản của bạn đang "thao túng" <strong>{user?.full_name}</strong>. Hãy cẩn trọng.
              </p>
            </div>
          </div>
        }
        confirmText="Xác nhận Nhập vai"
        onConfirm={handleImpersonate}
        onClose={() => setShowImpersonateConfirm(false)}
      />
    </PageDetailLayout>
  );
}
